import { useMemo } from "react";
import type { FilterState } from "@/types/filters";
import type { SchedulingRow } from "@/types/scheduling";
import type { ComputedMetrics } from "@/types/metrics";
import {
  applyBaseFilter,
  filterRowsByDateRange,
  filterRowsByScope,
  computeMetrics,
} from "@/lib/metrics";
import { EMPTY_RANGE, getPriorYearRange } from "@/lib/periods";

export interface MetricsResult {
  current: ComputedMetrics;
  priorYear: ComputedMetrics;
  nvaAvg: ComputedMetrics;
}

export function useMetrics(rows: SchedulingRow[], filters: FilterState): MetricsResult {
  // Destructured OUTSIDE the memo so the dependency list can name the fields this
  // computation actually reads. Depending on the whole `filters` object recomputed
  // everything whenever any field changed identity -- including capacityView and
  // capacityMetric, which are display-only. Switching the Capacity cards from
  // Average to Total re-derived every metric over ~128k rows to move a label.
  const { customStart, customEnd, scope, appointmentTypes, period, excludeZeroApptDays } = filters;
  return useMemo(() => {
    const base = applyBaseFilter(rows, excludeZeroApptDays);
    const pyRange =
      getPriorYearRange({ startDate: customStart, endDate: customEnd }, period) ?? EMPTY_RANGE;

    const currentScoped = filterRowsByScope(
      filterRowsByDateRange(base, customStart, customEnd),
      scope
    );
    const priorYearScoped = filterRowsByScope(
      filterRowsByDateRange(base, pyRange.startDate, pyRange.endDate),
      scope
    );
    // NVA avg = all locations (no scope filter), same date range
    const nvaCurrentPeriod = filterRowsByDateRange(base, customStart, customEnd);

    return {
      current: computeMetrics(currentScoped, customStart, customEnd, appointmentTypes),
      priorYear: computeMetrics(
        priorYearScoped,
        pyRange.startDate,
        pyRange.endDate,
        appointmentTypes
      ),
      nvaAvg: computeMetrics(nvaCurrentPeriod, customStart, customEnd, appointmentTypes),
    };
    // Depending on the whole `filters` object recomputed everything whenever ANY
    // field changed identity -- including capacityView and capacityMetric, which
    // are display-only. Switching the Capacity cards from Average to Total
    // re-derived every metric over ~128k rows to move a label.
    //
    // Listed explicitly instead, so only a field this computation actually reads
    // invalidates it.
  }, [rows, excludeZeroApptDays, customStart, customEnd, period, scope, appointmentTypes]);
}
